// Load Pyodide dynamically from CDN to avoid webpack bundling issues
// We don't import pyodide at the top level to prevent webpack from trying to bundle it
let pyodideInstance = null;
let loadingPromise = null;

/**
 * Initialize Pyodide Python runtime
 * This loads the Python interpreter into the browser from CDN
 */
export const initPythonRuntime = async () => {
  if (pyodideInstance) {
    return pyodideInstance;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = (async () => {
    try {
      console.log('Loading Pyodide Python runtime from CDN...');
      
      // Dynamically load Pyodide from CDN to avoid webpack bundling
      if (typeof window === 'undefined') {
        throw new Error('Pyodide can only run in browser environment');
      }
      
      // Check if Pyodide is already loaded
      if (!window.loadPyodide) {
        // Load the Pyodide script from CDN
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/pyodide.js';
          script.onload = () => {
            // Wait a bit for Pyodide to initialize
            setTimeout(resolve, 100);
          };
          script.onerror = () => reject(new Error('Failed to load Pyodide from CDN'));
          document.head.appendChild(script);
        });
      }
      
      // Use the globally loaded Pyodide
      if (!window.loadPyodide) {
        throw new Error('Pyodide failed to load');
      }
      
      pyodideInstance = await window.loadPyodide({
        indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.24.1/full/'
      });
      
      console.log('Pyodide loaded successfully');
      return pyodideInstance;
    } catch (error) {
      console.error('Failed to load Pyodide:', error);
      loadingPromise = null;
      throw error;
    }
  })();

  return loadingPromise;
};

/**
 * Execute Python code using Pyodide
 * @param {string} code - Python code to execute
 * @param {number} timeout - Execution timeout in milliseconds (default: 10000)
 * @returns {Promise<{output: string, error: string|null}>}
 */
export const runPythonCode = async (code, timeout = 10000) => {
  try {
    // Initialize if not already loaded
    const pyodide = await initPythonRuntime();

    // Set up output capture
    pyodide.runPython(`
import sys
from io import StringIO

# Create a StringIO buffer to capture stdout
_output_buffer = StringIO()
_original_stdout = sys.stdout
sys.stdout = _output_buffer
`);

    // Execute code with timeout
    const executionPromise = (async () => {
      try {
        // Run the actual code
        pyodide.runPython(code);
        
        // Get captured output
        const output = pyodide.runPython('_output_buffer.getvalue()');
        
        // Restore stdout
        pyodide.runPython('sys.stdout = _original_stdout');
        
        return {
          output: output || '',
          error: null
        };
      } catch (execError) {
        // Get any output before the error
        let output = '';
        try {
          output = pyodide.runPython('_output_buffer.getvalue()');
        } catch (e) {
          // Ignore
        }
        
        // Restore stdout
        try {
          pyodide.runPython('sys.stdout = _original_stdout');
        } catch (e) {
          // Ignore restore errors
        }
        
        // Check if it's a Python exception
        const errorMsg = execError.message || String(execError);
        if (errorMsg.includes('Traceback') || errorMsg.includes('Error') || errorMsg.includes('Exception')) {
          return {
            output: output,
            error: errorMsg
          };
        }
        
        return {
          output: output,
          error: `Python Error: ${errorMsg}`
        };
      }
    })();

    // Add timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Execution timeout')), timeout);
    });

    try {
      const result = await Promise.race([executionPromise, timeoutPromise]);
      return result;
    } catch (error) {
      if (error.message === 'Execution timeout') {
        return {
          output: '',
          error: `Execution timed out after ${timeout}ms`
        };
      }
      throw error;
    }
  } catch (error) {
    return {
      output: '',
      error: error.message || 'Failed to execute Python code'
    };
  }
};

/**
 * Check if Python runtime is available
 */
export const isPythonRuntimeAvailable = () => {
  return pyodideInstance !== null;
};

